import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table"
import { useState } from "react"
import { useAsync, useAsyncCallback } from "react-async-hook"
import { Link, useLocation, useNavigate } from "react-router-dom"
import { useApp } from "../../AppProvider"
import { useApi } from "../../hooks/useApi"
import { Organization } from "../../types/Organization"
import { classNames } from "../../utils/classNames"
import { ConfirmModal } from "../ConfirmModal"
import { Button } from "../common"
import { LoadingMessage } from "../common/Feedback/LoadingMessage"

const columnHelper = createColumnHelper<Organization>()
const columns = [
  columnHelper.accessor("name", {
    header: "Name",
  }),
  columnHelper.accessor("active", {
    header: "Active",
    cell: (info) => <span>{info.getValue() ? "Yes" : "No"}</span>,
  }),
  columnHelper.display({
    id: "actions",
    cell: (props: any) => {
      const {
        navigate,
        row: { original: organization },
        setOrganizationToDelete,
      } = props
      return (
        <div className="flex items-center justify-end gap-x-4">
          <Link
            to={`/private/organizations/${organization._id}/`}
            className="text-primary-600 hover:text-primary-900 text-sm font-medium"
          >
            Details
          </Link>
          <Button
            variant="outline"
            onClick={() => navigate(`/private/organizations/${organization._id}/edit`)}
          >
            Edit
          </Button>
          <Button variant="danger" onClick={() => setOrganizationToDelete(organization)}>
            Delete
          </Button>
        </div>
      )
    },
  }),
]

export const OrganizationList = () => {
  const { getOrganizations, deleteOrganization } = useApi()
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const [organizationToDelete, setOrganizationToDelete] = useState<Organization | undefined>(undefined)
  const { addToast } = useApp()

  const organizationsAsync = useAsync(async () => getOrganizations(0, 20), [])
  const organizations: Organization[] = organizationsAsync.result?.organizations || []

  const table = useReactTable({
    data: organizations,
    columns,
    getCoreRowModel: getCoreRowModel(),
  })

  const deleteOrganizationAsync = useAsyncCallback(async () => {
    if (!organizationToDelete) return
    await deleteOrganization(organizationToDelete._id)
    organizationsAsync.execute()
    setOrganizationToDelete(undefined)
    addToast("Success", "Organization deleted", "success")
  })

  return (
    <div className="card overflow-hidden">
      <div className="flex items-start justify-between">
        <div className="text-lg font-semibold text-gray-900">Organizations</div>
        <Button onClick={() => navigate(`/private/organizations/new/edit`)}>Add</Button>
      </div>

      <ConfirmModal
        header="Delete Organization"
        body={
          <>
            Are you sure you want to delete the organization <strong>{organizationToDelete?.name}</strong>?
          </>
        }
        open={!!organizationToDelete}
        setOpen={() => setOrganizationToDelete(undefined)}
        onCancel={() => setOrganizationToDelete(undefined)}
        onConfirm={deleteOrganizationAsync.execute}
        confirmVariant="danger"
        loading={deleteOrganizationAsync.loading}
      />

      {organizationsAsync.loading ? (
        <div className="mx-auto my-3">
          <LoadingMessage message="Loading organizations..." />
        </div>
      ) : (
        <>
          <div className="flow-root">
            <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
              <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    {table.getHeaderGroups().map((headerGroup) => (
                      <tr key={headerGroup.id}>
                        {headerGroup.headers.map((header, i) => (
                          <th
                            scope="col"
                            className={classNames(
                              "relative py-3.5 text-left text-sm font-semibold text-gray-900",
                              i === 0
                                ? "pr-3 pl-4 sm:pl-0"
                                : i === headerGroup.headers.length - 1
                                  ? "pr-4 pl-3 sm:pr-0"
                                  : "px-3",
                            )}
                            key={header.id}
                          >
                            {header.isPlaceholder
                              ? null
                              : flexRender(header.column.columnDef.header, header.getContext())}
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {table.getRowModel().rows.map((row) => (
                      <tr key={row.id}>
                        {row.getVisibleCells().map((cell, i) => (
                          <td
                            className={classNames(
                              "py-4 text-sm font-medium whitespace-nowrap text-gray-900",
                              i === 0
                                ? "pr-3 pl-4 sm:pl-0"
                                : i === row.getVisibleCells().length - 1
                                  ? "pr-4 pl-3 sm:pr-0"
                                  : "px-3",
                            )}
                            key={cell.id}
                          >
                            {flexRender(cell.column.columnDef.cell, {
                              ...cell.getContext(),
                              navigate,
                              pathname,
                              setOrganizationToDelete,
                            })}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
